import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { StandardCase } from "@src/lms/jelly/extensions/layouts/standard-case/StandardCase";
import { useUi, useBlocks } from "@jelly/lesson";
import { Accordion, AccordionItem } from "@src/lms/jelly/extensions/ui";
import { useIntl } from "react-intl";
import styles from "./Case1TechnicalPov.module.scss";
import { AccordionContext } from "@src/lms/jelly/extensions/ui/accordion/AccordionContext";
import * as appConfig from "appConfig";

import { BuffModal } from "@src/lms/jelly/extensions/layouts/_lesson/case7/artistic-pov/components/BuffModal";
import { messages } from "@src/lms/jelly/extensions/layouts/_lesson/case1/Case1TechnicalPov.messages";
import { SpatialCarousel } from "@src/lms/jelly/extensions/layouts";

interface Case1TechnicalPovProps {
  title: string;
}

const ACCORDION_IDS = {
  productionProcess: "production-process",
  interviews: "interviews",
  recordings: "recordings",
} as const;

type CaseSlide = "default" | "venue" | "speaker-distribution" | "signal-flow" | "tuning" | "recordings" | "interviews";

interface Case1ContextData {
  setActiveSlide: (slide: CaseSlide) => void;
  activeSlide: CaseSlide;
}

const Case1Context = React.createContext<Case1ContextData>({
  activeSlide: "default",
  setActiveSlide: () => {
    throw new Error("Not implemented!");
  },
});

export const Case1TechnicalPov: React.FC<Case1TechnicalPovProps> = ({ title }) => {
  const [activeSlide, setActiveSlide] = useState<CaseSlide>("default");

  return (
    <Case1Context.Provider
      value={{
        setActiveSlide,
        activeSlide,
      }}
    >
      <Case1TechnicalPovContent title={title} />
    </Case1Context.Provider>
  );
};

const Case1TechnicalPovContent: React.FC<Case1TechnicalPovProps> = ({ title }) => {
  const accordion = <CaseAccordion title={title} />;
  const { activeSlide } = useContext(Case1Context);
  const block = useBlocks();

  let content: React.ReactNode = activeSlide;

  switch (activeSlide) {
    case "default":
      content = (
        // @ts-expect-error We cannot override layouts using Contexts :/
        <SpatialCarousel showBreadcrumbs>
          {[
            <div key={1} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_full.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
            <div key={2} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_studio.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
            <div key={3} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_studio_2.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
            <div key={4} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_kitchen.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
            <div key={5} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_studio_3.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
            <div key={6} className={styles.carouselItem}>
              <img
                className={styles.image}
                src={`${appConfig.STATIC_ASSETS_URL}/lms/case/case01/truck_full_interior.png`}
                alt="Atomos Truck schematic image"
              />
            </div>,
          ]}
        </SpatialCarousel>
      );
      break;

    case "interviews":
      content = <block.video src="https://www.youtube.com/watch?v=SsvBkcJcr2c" provider="youtube" />;
      break;
  }

  return (
    <StandardCase title={title} leftComponent={accordion}>
      <div className={styles.content}>{content}</div>
    </StandardCase>
  );
};

const CaseAccordion: React.FC<Case1TechnicalPovProps> = () => {
  const ui = useUi();
  const { formatMessage } = useIntl();

  //todo: refactor modal state management
  const [buffModalOpen, setBuffModalOpen] = useState(false);
  const handleBuffModalToggle = useCallback(() => setBuffModalOpen((open) => !open), []);

  const INTERVIEWERS = [
    {
      description: messages.interviews1Description,
      bioCallback: handleBuffModalToggle,
      thumbnail: "buff.png",
      videoSrc: "https://www.youtube.com/watch?v=_U0dEK1R2u0",
    },
  ];

  return (
    <div className={styles.leftPanel}>
      <ui.Scrollbars>
        <div className={styles.leftPanelWrapper}>
          <div className={styles.accordion}>
            <Accordion allowMultiOpen={false} defaultOpen={[]}>
              <MainAccordionObserver />
              <AccordionItem
                id={ACCORDION_IDS.productionProcess}
                title={formatMessage(messages.accordionItem1Title)}
                unmountChildrenWhenHidden
              >
                {formatMessage(messages.accordionItem1Description)}
              </AccordionItem>
              <AccordionItem id={ACCORDION_IDS.interviews} title={formatMessage(messages.accordionItem2Title)}>
                <div className={styles.interviews}>
                  {INTERVIEWERS.map((data) => (
                    <>
                      <div className={styles.entry}>
                        <div className={styles.text}>{formatMessage(data.description)}</div>
                      </div>
                      <div className={styles.bioButtonWrapper}>
                        <a href="https://www.120db.pl/" target="_blank" rel="noreferrer">
                          <ui.Button shape={"normal"} theme={"primary"} size={"small"}>
                            120db
                          </ui.Button>
                        </a>
                      </div>
                    </>
                  ))}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </ui.Scrollbars>

      <BuffModal visible={buffModalOpen} onClose={handleBuffModalToggle} />
    </div>
  );
};

const MainAccordionObserver: React.FC = () => {
  const { setActiveSlide } = useContext(Case1Context);
  const accordionContext = useContext(AccordionContext);
  const previouslyOpen = useRef<string | null>(null);

  useEffect(() => {
    const openItem = accordionContext.openIds[0] ?? null;
    const userOpenedAnotherItem = previouslyOpen.current != openItem;
    if (userOpenedAnotherItem) {
      previouslyOpen.current = openItem;
      let slide: CaseSlide = "default";
      switch (openItem) {
        case ACCORDION_IDS.recordings:
          slide = "recordings";
          break;
        case ACCORDION_IDS.interviews:
          slide = "interviews";
          break;
      }

      setActiveSlide(slide);
    }
  }, [accordionContext.openIds, setActiveSlide]);

  return null;
};
